import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, ArrowRight, Phone, Mail, Search, Trash2, Edit, Plus, Building2, User, Download, FileSpreadsheet } from 'lucide-react';
import { getCustomers, addCustomer, updateCustomer, updateCustomerNumber, deleteCustomer, importCustomersFromLogs, exportCustomersExcel, authMe } from '../api';
import { matchesCustomer } from '../lib/customerSearch';
import { toast } from '../hooks/use-toast';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [numberInput, setNumberInput] = useState('');

  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', customerType: 'private'
  });

  useEffect(() => {
    loadCustomers();
    authMe().then(u => setIsManager(u?.role === 'manager')).catch(() => setIsManager(false));
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      if (data.length === 0) {
        // Auto-import from daily logs on first load if empty
        const result = await importCustomersFromLogs();
        if (result.imported > 0) {
          toast({ title: 'ייבוא אוטומטי', description: result.message });
          const refreshed = await getCustomers();
          setCustomers(refreshed);
        } else {
          setCustomers([]);
        }
      } else {
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const result = await importCustomersFromLogs();
      toast({ title: 'ייבוא הושלם', description: result.message });
      await loadCustomers();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לייבא לקוחות', variant: 'destructive' });
    } finally {
      setIsImporting(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingCustomer(null);
    setForm({ firstName: '', lastName: '', phone: '', email: '', customerType: 'private', vatExempt: false });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (customer) => {
    setEditingCustomer(customer);
    setNumberInput(customer.customerNumber ? String(customer.customerNumber) : '');
    let firstName = customer.firstName || '';
    let lastName = customer.lastName || '';
    // Auto-split: if lastName is empty and firstName has a space, split it
    if (!lastName && firstName.includes(' ')) {
      const parts = firstName.split(' ');
      firstName = parts[0];
      lastName = parts.slice(1).join(' ');
    }
    setForm({
      firstName,
      lastName,
      phone: customer.phone || '',
      email: customer.email || '',
      customerType: customer.customerType || 'private',
      vatExempt: !!customer.vatExempt
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) {
      toast({ title: 'שגיאה', description: 'שם פרטי נדרש', variant: 'destructive' });
      return;
    }
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, form);
        const newNumber = parseInt(numberInput, 10);
        if (isManager && numberInput.trim() && newNumber !== editingCustomer.customerNumber) {
          await updateCustomerNumber(editingCustomer.id, newNumber);
        }
        toast({ title: 'עודכן', description: 'לקוח עודכן בהצלחה' });
      } else {
        await addCustomer(form);
        toast({ title: 'נוסף', description: 'לקוח חדש נוסף בהצלחה' });
      }
      setIsDialogOpen(false);
      await loadCustomers();
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן לשמור', variant: 'destructive' });
      await loadCustomers();
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`למחוק את ${name}?`)) {
      try {
        await deleteCustomer(id);
        toast({ title: 'נמחק', description: 'הלקוח נמחק בהצלחה' });
        await loadCustomers();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
      }
    }
  };

  const handleToggleBusiness = async (customer) => {
    const newType = customer.customerType === 'business' ? 'private' : 'business';
    try {
      const updated = await updateCustomer(customer.id, { ...customer, customerType: newType });
      setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, ...updated } : c));
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  const filtered = customers.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return matchesCustomer(c, q) || (c.phone || '').includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const businessCount = customers.filter(c => c.customerType === 'business').length;
  const privateCount = customers.filter(c => c.customerType === 'private').length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען לקוחות...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-5xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold flex items-center gap-3">
                <Users className="w-8 h-8" />
                לקוחות
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={() => { window.location.href = exportCustomersExcel(); }}
                  variant="outline"
                  className="border-white text-white hover:bg-white/20"
                  data-testid="export-customers-btn"
                >
                  <FileSpreadsheet className="ml-2 w-4 h-4" />
                  ייצוא לאקסל
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={isImporting}
                  variant="outline"
                  className="border-white text-white hover:bg-white/20"
                  data-testid="import-customers-btn"
                >
                  <Download className="ml-2 w-4 h-4" />
                  {isImporting ? 'מייבא...' : 'ייבוא מרישום יומי'}
                </Button>
                <Button
                  onClick={handleOpenAdd}
                  className="bg-white text-teal-700 hover:bg-gray-100"
                  data-testid="add-customer-btn"
                >
                  <Plus className="ml-2 w-4 h-4" />
                  לקוח חדש
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Stats */}
            <div className="flex gap-4 mb-6">
              <div className="flex items-center gap-2 bg-teal-50 px-4 py-2 rounded-lg border border-teal-200">
                <Users className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-semibold text-teal-800">סה&quot;כ: {customers.length}</span>
              </div>
              <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200">
                <User className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-800">פרטי: {privateCount}</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-800">עסקי: {businessCount}</span>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="חיפוש לפי מספר לקוח, שם, טלפון או אימייל..."
                className="w-full pr-10 pl-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors bg-white"
                data-testid="customer-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg font-bold"
                >
                  ×
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-gray-500 mb-4">מציג {filtered.length} מתוך {customers.length} לקוחות</p>
            )}

            {/* Table */}
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery ? `לא נמצאו לקוחות התואמים ל"${searchQuery}"` : 'אין לקוחות. הוסף לקוח חדש או ייבא מרישום יומי.'}
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                      <th className="p-3 text-center font-bold w-20">מס' לקוח</th>
                      <th className="p-3 text-right font-bold">שם פרטי</th>
                      <th className="p-3 text-right font-bold">שם משפחה</th>
                      <th className="p-3 text-center font-bold">סוג</th>
                      <th className="p-3 text-right font-bold">טלפון</th>
                      <th className="p-3 text-right font-bold">אימייל</th>
                      <th className="p-3 text-center font-bold w-24">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((customer, idx) => (
                      <tr key={customer.id} className={idx % 2 === 0 ? 'bg-white hover:bg-teal-50' : 'bg-gray-50 hover:bg-teal-50'}>
                        <td className="p-3 text-center border-b">
                          <button
                            type="button"
                            onClick={() => navigate(`/customer-card/${customer.id}`)}
                            title="פתח כרטיס לקוח"
                            className={`inline-block min-w-[2.5rem] px-2 py-1 rounded-md text-sm font-bold font-mono underline-offset-4 hover:underline hover:scale-110 transition-transform ${customer.customerType === 'business' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}
                            data-testid={`customer-number-${customer.id}`}
                          >
                            {customer.customerNumber ?? '-'}
                          </button>
                        </td>
                        <td className="p-3 font-semibold text-gray-900 border-b">
                          <button type="button" onClick={() => navigate(`/customer-card/${customer.id}`)} className="hover:text-teal-700 hover:underline underline-offset-4" data-testid={`customer-name-link-${customer.id}`}>{customer.firstName}</button>
                        </td>
                        <td className="p-3 text-gray-700 border-b">{customer.lastName}</td>
                        <td className="p-3 text-center border-b">
                          <button
                            onClick={() => handleToggleBusiness(customer)}
                            className={`text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer transition-all hover:scale-105 ${customer.customerType === 'business' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 hover:bg-blue-50 hover:text-blue-500'}`}
                            title={customer.customerType === 'business' ? 'לחץ להחזיר לפרטי' : 'לחץ לסמן כעסקי'}
                            data-testid={`toggle-business-${customer.id}`}
                          >
                            {customer.customerType === 'business' ? 'עסקי' : 'פרטי'}
                          </button>
                        </td>
                        <td className="p-3 border-b">
                          {customer.phone ? (
                            <span className="flex items-center gap-1 text-gray-700">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />{customer.phone}
                            </span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 border-b" dir="ltr">
                          {customer.email ? (
                            <span className="flex items-center gap-1 text-gray-700 justify-end">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />{customer.email}
                            </span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 border-b text-center">
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => handleOpenEdit(customer)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-customer-${customer.id}`}>
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(customer.id, `${customer.firstName} ${customer.lastName}`)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-customer-${customer.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCustomer ? 'עריכת לקוח' : 'הוספת לקוח חדש'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {editingCustomer && (
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">מספר לקוח</label>
                <Input
                  type="number"
                  min="1"
                  value={numberInput}
                  onChange={(e) => setNumberInput(e.target.value)}
                  disabled={!isManager}
                  dir="ltr"
                  className="font-mono font-bold"
                  data-testid="customer-number-input"
                />
                <p className="text-xs text-gray-500 mt-1" data-testid="customer-number-hint">
                  {isManager ? 'עסקי – מספר זוגי, פרטי – מספר אי-זוגי. אם תשנה את סוג הלקוח, הוא יקבל מספר חדש באופן אוטומטי.' : 'רק מנהל יכול לשנות מספר לקוח'}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">שם פרטי *</label>
                <Input
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="שם פרטי"
                  data-testid="customer-first-name"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">שם משפחה</label>
                <Input
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="שם משפחה"
                  data-testid="customer-last-name"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">טלפון</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="050-0000000"
                dir="ltr"
                data-testid="customer-phone"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">אימייל</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                dir="ltr"
                data-testid="customer-email"
              />
            </div>
            <label className="flex flex-col gap-1 p-3 rounded-lg border-2 border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="customer-vat-exempt-label">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <input type="checkbox" checked={!!form.vatExempt} onChange={(e) => setForm({ ...form, vatExempt: e.target.checked })} className="w-4 h-4 accent-slate-700" data-testid="customer-vat-exempt-checkbox" />
                <span className="text-sm font-semibold text-gray-700">פטור ממע"מ</span>
              </span>
              <span className="text-xs text-gray-500">"ללא מע"מ" יסומן אוטומטית בטפסים חדשים ללקוח זה</span>
            </label>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">סוג לקוח</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, customerType: 'private' })}
                  className={`flex-1 py-3 rounded-lg border-2 font-semibold transition-all flex items-center justify-center gap-2 ${form.customerType === 'private' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  data-testid="customer-type-private"
                >
                  <User className="w-4 h-4" />
                  פרטי
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, customerType: 'business' })}
                  className={`flex-1 py-3 rounded-lg border-2 font-semibold transition-all flex items-center justify-center gap-2 ${form.customerType === 'business' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  data-testid="customer-type-business"
                >
                  <Building2 className="w-4 h-4" />
                  עסקי
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSave}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                data-testid="save-customer-btn"
              >
                {editingCustomer ? 'עדכן' : 'הוסף'}
              </Button>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="px-6">
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
